import { useEffect, useState } from "react";
import api from "@/lib/api";
import { RichEditor } from "@/components/RichEditor";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ScontoCampaigns from "@/components/ScontoCampaigns";
import { Megaphone, Plus, Send, Trash2, Pencil, Users, Mail, Loader2, CheckCircle2, FileText, Percent } from "lucide-react";
import { toast } from "sonner";

const STATO = {
  bozza: { label: "Bozza", cls: "bg-zinc-700/40 text-zinc-300" },
  inviata: { label: "Inviata", cls: "bg-[#39FF14]/15 text-[#39FF14]" },
};

export default function Marketing() {
  const [campaigns, setCampaigns] = useState([]);
  const [recipients, setRecipients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // campaign obj or {new:true}
  const [confirmSend, setConfirmSend] = useState(null);
  const [testFor, setTestFor] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([api.get("/marketing/campaigns"), api.get("/marketing/recipients")]);
      setCampaigns(c.data); setRecipients(r.data.count);
    } catch (e) { toast.error("Errore di caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.oggetto?.trim()) { toast.error("Inserisci l'oggetto"); return; }
    setBusy(true);
    try {
      if (editing.id) await api.put(`/marketing/campaigns/${editing.id}`, { oggetto: editing.oggetto, contenuto_html: editing.contenuto_html || "" });
      else await api.post("/marketing/campaigns", { oggetto: editing.oggetto, contenuto_html: editing.contenuto_html || "" });
      toast.success("Campagna salvata"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/marketing/campaigns/${confirmSend.id}/send`, { origin_url: window.location.origin });
      toast.success(`Inviata a ${r.data.inviati} clienti${r.data.falliti ? `, ${r.data.falliti} falliti` : ""}`);
      setConfirmSend(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); }
    finally { setBusy(false); }
  };

  const doTest = async () => {
    if (!testTo.includes("@")) { toast.error("Email non valida"); return; }
    setBusy(true);
    try {
      await api.post(`/marketing/campaigns/${testFor.id}/test`, { to: testTo, origin_url: window.location.origin });
      toast.success(`Email di prova inviata a ${testTo}`); setTestFor(null); setTestTo("");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio prova"); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la campagna "${c.oggetto}"?`)) return;
    try { await api.delete(`/marketing/campaigns/${c.id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="space-y-6" data-testid="marketing-page">
      <div>
        <h1 className="font-display text-4xl tracking-tight flex items-center gap-2"><Megaphone className="h-7 w-7 text-[#f98125]" /> MARKETING</h1>
        <p className="text-zinc-500 text-sm mt-1">Newsletter ai clienti e campagne sconto con codice promozionale.</p>
      </div>

      <Tabs defaultValue="newsletter" className="w-full">
        <TabsList className="bg-[#141414] border border-zinc-800 rounded-sm p-1">
          <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Mail className="h-4 w-4 mr-1.5" /> Newsletter</TabsTrigger>
          <TabsTrigger value="sconti" data-testid="tab-campagne-sconto" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm"><Percent className="h-4 w-4 mr-1.5" /> Campagne sconto</TabsTrigger>
        </TabsList>

        <TabsContent value="newsletter" className="mt-5 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="bg-[#141414] border border-zinc-800 rounded-sm p-4 flex items-center gap-3" data-testid="mkt-recipients">
              <Users className="h-5 w-5 text-[#f98125]" />
              <div className="text-sm">Destinatari con consenso marketing: <b className="text-[#f98125] text-lg">{recipients}</b></div>
            </div>
            <button onClick={() => setEditing({ new: true, oggetto: "", contenuto_html: "" })} data-testid="mkt-new-btn"
              className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-4 py-2.5 text-sm hover:bg-[#d96a12]">
              <Plus className="h-4 w-4" /> Nuova campagna
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#f98125]" /></div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-20 text-zinc-500"><FileText className="h-10 w-10 mx-auto mb-3 text-zinc-700" />Nessuna campagna. Creane una per iniziare.</div>
          ) : (
            <div className="space-y-2" data-testid="mkt-list">
              {campaigns.map((c) => (
            <div key={c.id} data-testid={`mkt-camp-${c.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{c.oggetto}</span>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATO[c.stato]?.cls}`}>{STATO[c.stato]?.label || c.stato}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {c.stato === "inviata" ? `Inviata a ${c.inviati}/${c.destinatari}${c.falliti ? ` · ${c.falliti} falliti` : ""}` : "Bozza non ancora inviata"}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setTestFor(c)} data-testid={`mkt-test-${c.id}`} title="Invia prova" className="h-9 w-9 flex items-center justify-center rounded-sm border border-zinc-700 text-zinc-300 hover:border-[#f98125]"><Mail className="h-4 w-4" /></button>
                {c.stato === "bozza" && <button onClick={() => setEditing(c)} data-testid={`mkt-edit-${c.id}`} title="Modifica" className="h-9 w-9 flex items-center justify-center rounded-sm border border-zinc-700 text-zinc-300 hover:border-[#f98125]"><Pencil className="h-4 w-4" /></button>}
                {c.stato === "bozza" && <button onClick={() => setConfirmSend(c)} data-testid={`mkt-send-${c.id}`} className="flex items-center gap-1.5 rounded-sm bg-[#f98125] text-white font-bold px-3 py-2 text-sm hover:bg-[#d96a12]"><Send className="h-4 w-4" /> Invia</button>}
                <button onClick={() => remove(c)} data-testid={`mkt-del-${c.id}`} title="Elimina" className="h-9 w-9 flex items-center justify-center rounded-sm border border-zinc-700 text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="sconti" className="mt-5">
          <ScontoCampaigns />
        </TabsContent>
      </Tabs>

      {/* editor dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-2xl" data-testid="mkt-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?.id ? "Modifica campagna" : "Nuova campagna"}</DialogTitle>
            <DialogDescription>Scrivi l'oggetto e il contenuto. Le email verranno inviate ai clienti con consenso marketing.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500">Oggetto</label>
                <Input value={editing.oggetto} onChange={(e) => setEditing({ ...editing, oggetto: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="mkt-oggetto" placeholder="Es. Estate in e-bike: -20% questa settimana!" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500">Contenuto</label>
                <div className="mt-1.5"><RichEditor value={editing.contenuto_html} onChange={(html) => setEditing((e) => ({ ...e, contenuto_html: html }))} testid="mkt-editor" /></div>
                <p className="text-[11px] text-zinc-500 mt-1.5">Il saluto al cliente e il link di disiscrizione vengono aggiunti automaticamente all'email.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Annulla</button>
            <button onClick={save} disabled={busy} data-testid="mkt-save" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* test dialog */}
      <Dialog open={!!testFor} onOpenChange={(v) => !v && setTestFor(null)}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-md" data-testid="mkt-test-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Invia email di prova</DialogTitle><DialogDescription>Ricevi una copia di prova di "{testFor?.oggetto}".</DialogDescription></DialogHeader>
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="tua@email.com" className="bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="mkt-test-to" />
          <DialogFooter>
            <button onClick={() => setTestFor(null)} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Annulla</button>
            <button onClick={doTest} disabled={busy} data-testid="mkt-test-send" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Invia prova</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* confirm send */}
      <Dialog open={!!confirmSend} onOpenChange={(v) => !v && setConfirmSend(null)}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-md" data-testid="mkt-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Send className="h-5 w-5 text-[#f98125]" /> Confermi l'invio?</DialogTitle>
            <DialogDescription>La campagna "{confirmSend?.oggetto}" verrà inviata a <b>{recipients}</b> clienti con consenso marketing. L'operazione non è reversibile.</DialogDescription></DialogHeader>
          <DialogFooter>
            <button onClick={() => setConfirmSend(null)} className="rounded-sm border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800">Annulla</button>
            <button onClick={doSend} disabled={busy} data-testid="mkt-confirm-send" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-4 py-2 text-sm hover:bg-[#d96a12] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Invia ora</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
